import { useEffect, useRef } from "react"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

// Visual style slots: first track = solid, second = dotted, third = dashed, rest = neutral
export const trackStyles = {
  mentor:  { color: "#FF4000", borderStyle: "solid",  opacity: 1 },
  founder: { color: "#FF4000", borderStyle: "dotted", opacity: 0.65 },
  shared:  { color: "#FF4000", borderStyle: "dashed", opacity: 0.45 },
  neutral: { color: "rgba(255,255,255,0.5)", borderStyle: "solid", opacity: 1 },
}

const STYLE_SLOTS = ["mentor", "founder", "shared"]

// Assign a visual style slot to each track id by position
export function buildStyleMap(tracks) {
  const map = {}
  if (tracks) tracks.forEach((t, i) => { map[t.id] = STYLE_SLOTS[i] || "neutral" })
  return map
}

export function capitalize(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : ""
}

export function deriveTimeline(sessions, tracks, styleMap) {
  const getLabel = id => tracks?.find(t => t.id === id)?.label || capitalize(id)

  const rows = []
  for (const session of sessions) {
    if (session.sub_sessions) {
      for (const sub of session.sub_sessions) {
        const parts = [sub.time_of_day ? capitalize(sub.time_of_day) : null, sub.duration]
        if (sub.optional) parts.push("optional")
        rows.push({
          month: String(session.month),
          session: sub.id === "mixer" ? sub.title : `${session.title} (${sub.title})`,
          detail: parts.filter(Boolean).join(" \u00b7 "),
          track: getLabel(sub.track),
          trackStyle: styleMap[sub.track] || "neutral",
          format: capitalize(session.format),
          purpose: sub.objectives?.[0] || "",
        })
      }
    } else {
      const isRetro = session.type === "retrospective"
      const styleKey = isRetro ? "neutral" : (styleMap[session.track] || "neutral")

      let title = session.title
      if (session.topic) {
        title = `${session.title} (${session.topic.split("\u2014")[0].trim()})`
      }

      const dur = session.duration || ""
      let format
      if (dur.toLowerCase().startsWith("async")) format = "Async survey"
      else if (session.format === "online" && dur) format = `Online \u00b7 ${dur}`
      else format = capitalize(session.format)

      rows.push({
        month: String(session.month),
        session: title,
        detail: "",
        track: isRetro ? "All" : getLabel(session.track),
        trackStyle: styleKey,
        format,
        purpose: session.objectives?.[0] || session.description?.split(".")?.[0] || "",
      })
    }
  }
  return rows
}

export function deriveTopics(sessions) {
  const seen = new Set()
  const topics = []
  for (const session of sessions || []) {
    for (const t of session.topic_candidates || []) {
      if (!seen.has(t)) { seen.add(t); topics.push(t) }
    }
  }
  return topics
}

export function deriveIndicators(measurement, styleMap) {
  return (measurement?.leading_indicators || []).map(ind => ({
    styleKey: styleMap[ind.track] || ind.track || "neutral",
    label: ind.label,
    desc: ind.description,
  }))
}

// Animate a block into view on scroll
export function useScrollReveal() {
  const ref = useRef(null)
  useEffect(() => {
    if (!ref.current) return
    const ctx = gsap.context(() => {
      gsap.fromTo(ref.current,
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out",
          scrollTrigger: { trigger: ref.current, start: "top 88%" } }
      )
    })
    return () => ctx.revert()
  }, [])
  return ref
}
